// State tab — read-only snapshot of the mock connection's current state plus a few
// manual setters (theme/uiLanguage/aiLanguage/compactSidebar via setView, modeId via
// setMode). Snapshot includes registered tools + thread file summaries for quick
// copy-paste into bug reports.

import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './simulatorStateTab.html?raw';
import lang from 'dojo/_base/lang';
import _WidgetBaseSP from 'more/_WidgetBaseSP';

import { MockEvisio2Sdk } from "../../mockSdk/mockEvisio2Sdk";
import { MockEvisio2Connection, MockStateChannel } from "../../mockSdk/mockEvisio2Connection";

class simulatorStateTab extends compose(_WidgetBaseSP) {
    templateString: any = template;

    public sdk: MockEvisio2Sdk;
    public connection: MockEvisio2Connection;

    private themeSelect:           HTMLSelectElement;
    private compactSidebarCheckbox:HTMLInputElement;
    private uiLangInput:           HTMLInputElement;
    private aiLangInput:           HTMLInputElement;
    private applyViewBtn:          HTMLButtonElement;
    private modeIdInput:           HTMLInputElement;
    private applyModeBtn:          HTMLButtonElement;
    private snapshotDisplay:       HTMLPreElement;
    private copyJsonBtn:           HTMLButtonElement;
    private copyStatus:            HTMLSpanElement;

    private _stateChangeDisposer: (() => void) | null = null;

    constructor(args?: { sdk: MockEvisio2Sdk; connection: MockEvisio2Connection }){
        super(...(arguments as unknown as []));
        if (args){ this.sdk = args.sdk; this.connection = args.connection; }
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.applyViewBtn.addEventListener("click", lang.hitch(this, this._onApplyView));
        this.applyModeBtn.addEventListener("click", lang.hitch(this, this._onApplyMode));
        this.copyJsonBtn.addEventListener("click", lang.hitch(this, this._onCopyJson));

        this._stateChangeDisposer = this.connection.addStateChangeListener(lang.hitch(this, this._onStateChange));
        this._renderSnapshot();
        this._syncFormsFromState();
    }

    startup(): void { super.startup.apply(this, arguments as any); }

    destroy(): void {
        if (this._stateChangeDisposer){ try { this._stateChangeDisposer(); } catch { /* ignore */ } this._stateChangeDisposer = null; }
        super.destroy.apply(this, arguments as any);
    }

    private _onStateChange(_channel: MockStateChannel): void {
        // Snapshot covers every channel — re-render unconditionally on any change.
        this._renderSnapshot();
    }

    private _syncFormsFromState(): void {
        let snap = this.connection._snapshot();
        this.themeSelect.value           = snap.view.theme || "auto";
        this.compactSidebarCheckbox.checked = !!snap.view.compactSidebar;
        this.uiLangInput.value           = snap.view.uiLanguage || "";
        this.aiLangInput.value           = snap.view.aiLanguage || "";
        this.modeIdInput.value           = snap.modeId || "";
    }

    private _renderSnapshot(): void {
        let snap = this.connection._snapshot();
        let txt = "";
        try { txt = JSON.stringify(snap, null, 2); } catch { txt = String(snap); }
        this.snapshotDisplay.textContent = txt;
    }

    private _onApplyView(): void {
        let theme = this.themeSelect.value as ("light" | "dark" | "auto");
        let compactSidebar = this.compactSidebarCheckbox.checked;
        let uiLang = this.uiLangInput.value || undefined;
        let aiLang = this.aiLangInput.value || undefined;
        this.connection.setView({ theme: theme, compactSidebar: compactSidebar, uiLanguage: uiLang, aiLanguage: aiLang });
    }

    private _onApplyMode(): void {
        let v = this.modeIdInput.value;
        this.connection.setMode(v === "" ? null : v);
    }

    private async _onCopyJson(): Promise<void> {
        let txt = this.snapshotDisplay.textContent || "";
        try {
            await navigator.clipboard.writeText(txt);
            this.copyStatus.textContent = "copied";
            this.copyStatus.style.color = "#9bd07b";
        } catch (e: any){
            this.copyStatus.textContent = "failed: " + (e && e.message ? e.message : String(e));
            this.copyStatus.style.color = "#ff8c8c";
        }
    }
}

export default mdsWidget(simulatorStateTab);
